import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { optUrl } from '../ui/OptImg';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Loader2, X, Paintbrush, Upload, ImagePlus, Link2, FileText, ArrowUpDown, Sun, Moon, Save, Trash2, Scissors } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSavedRooms, roomToScene } from './savedRooms';
import { toast } from 'sonner';
import { resolveImageUrl } from '../../lib/imageCache';
import { ROOM_SCENES, DEFAULT_CUSTOM_QUAD } from './roomScenes';
import { classifyFabric } from './fabricLight';
import { RoomMockup } from './RoomMockup';
import { CornerEditor } from './CornerEditor';
import { FabricSelector } from './FabricSelector';
import { LightControls } from './LightControls';
import { saveLook } from './lookStore';
import { EmptyPreviewCard } from '../configurator/EmptyPreviewCard';
import { quoteTracker } from '../../lib/consultSource';

const API = process.env.REACT_APP_BACKEND_URL;

const findByCode = (collections, code) => {
  for (const coll of collections) for (const mat of coll.materials) {
    const color = mat.colors.find(c => c.code === code);
    if (color) return { color, material: mat, collection: coll };
  }
  return null;
};

export const FabricRoomPreview = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [collections, setCollections] = useState([]);
  const [loadingFabrics, setLoadingFabrics] = useState(true);
  const [customScene, setCustomScene] = useState(null);
  const [activeId, setActiveId] = useState(params.get('room') || 'living');
  const [selection, setSelection] = useState(null);
  const [filterOpacity, setFilterOpacity] = useState(0.85);
  const [openness, setOpenness] = useState(3);
  const [drop, setDrop] = useState(0.5);
  const [ambience, setAmbience] = useState(params.get('ambience') === 'night' ? 'night' : 'day');
  const fileRef = useRef(null);
  const pickerRef = useRef(null);
  const { user, token } = useAuth();
  const saved = useSavedRooms(token);
  const [savedQuads, setSavedQuads] = useState({});
  const [savingRoom, setSavingRoom] = useState(false);

  const scenes = useMemo(() => {
    const savedScenes = saved.rooms.map(r => { const sc = roomToScene(r); const q = savedQuads[r.id]; return q ? { ...sc, shades: [{ ...sc.shades[0], quad: q }] } : sc; });
    return [...ROOM_SCENES, ...savedScenes, ...(customScene ? [customScene] : [])];
  }, [customScene, saved.rooms, savedQuads]);
  const activeRoom = scenes.find(s => s.id === activeId) || ROOM_SCENES[0];
  const light = useMemo(() => (selection ? classifyFabric(selection.color, selection.material, selection.collection) : null), [selection]);
  const textureUrl = resolveImageUrl(selection?.color?.image_url, 512);
  const hasNight = Boolean(activeRoom.images?.night);
  const effectiveAmbience = hasNight ? ambience : 'day';

  useEffect(() => {
    axios.get(`${API}/api/public/fabric-browser`)
      .then(({ data }) => {
        const colls = data.collections || [];
        setCollections(colls);
        const code = params.get('fabric');
        if (code) {
          const found = findByCode(colls, code);
          if (found) applySelection(found);
        }
        const op = parseInt(params.get('openness'), 10);
        if ([1, 3, 5, 10].includes(op)) setOpenness(op);
        const d = parseFloat(params.get('drop'));
        if (!Number.isNaN(d)) setDrop(Math.min(1, Math.max(0, d)));
      })
      .catch(() => {})
      .finally(() => setLoadingFabrics(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applySelection = (sel) => {
    setSelection(prev => { if (!prev) setDrop(0.5); return sel; });
    const l = classifyFabric(sel.color, sel.material, sel.collection);
    if (l.mode === 'filtering') setFilterOpacity(l.opacity);
    if (l.mode === 'screen') setOpenness(prev => (l.options.includes(prev) ? prev : l.options[0]));
  };

  const cycleRoom = (dir) => {
    const idx = scenes.findIndex(r => r.id === activeRoom.id);
    setActiveId(scenes[(idx + dir + scenes.length) % scenes.length].id);
  };

  const onUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      setCustomScene({
        id: 'custom', name: 'Your Room', icon: ImagePlus, aspect: img.naturalHeight / img.naturalWidth, images: { day: url },
        shades: [{ label: 'Your Window', quad: DEFAULT_CUSTOM_QUAD }], editable: true,
      });
      setActiveId('custom');
      toast.success('Photo added — drag the four corners onto your window');
    };
    img.src = url;
    e.target.value = '';
  };

  const updateCustomQuad = useCallback((quad) => {
    setCustomScene(s => (s ? { ...s, shades: [{ ...s.shades[0], quad }] } : s));
  }, []);

  const onQuadChange = activeRoom.savedId
    ? (quad) => setSavedQuads(q => ({ ...q, [activeRoom.savedId]: quad }))
    : updateCustomQuad;

  const saveRoom = async () => {
    if (!user) { toast.info('Sign in to save your room'); navigate('/login?next=/fabrics?view=room'); return; }
    setSavingRoom(true);
    try {
      if (activeRoom.savedId) {
        await saved.update(activeRoom.savedId, { quad: activeRoom.shades[0].quad });
        toast.success('Window position updated');
      } else {
        const room = await saved.save(activeRoom.images.day, activeRoom.shades[0].quad, `My Room ${saved.rooms.length + 1}`);
        setCustomScene(null);
        setActiveId(`saved-${room.id}`);
        toast.success('Room saved to your account');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save room');
    } finally { setSavingRoom(false); }
  };

  const deleteRoom = async () => {
    if (!activeRoom.savedId) { setCustomScene(null); setActiveId('living'); return; }
    try {
      await saved.remove(activeRoom.savedId);
      setActiveId('living');
      toast.success('Room removed');
    } catch { toast.error('Could not remove room'); }
  };

  const orderSample = () => {
    const { color, material, collection } = selection;
    const entry = { color_id: color.id, color_name: color.name, color_code: color.code, hex_color: color.hex_color || '', image_url: color.image_url || '', material_name: material.name, collection_name: collection.collection_name, brand_name: collection.brand_name || '' };
    let list = [];
    try { list = JSON.parse(localStorage.getItem('sampleRequestColors') || '[]'); } catch { list = []; }
    if (!list.some(s => s.color_id === entry.color_id)) list.push(entry);
    localStorage.setItem('sampleRequestColors', JSON.stringify(list));
    toast.success(`${color.name} added to your free sample request`);
    navigate('/request-samples');
  };

  const buildLook = () => ({
    fabric_name: selection.color.name, fabric_code: selection.color.code,
    material_name: selection.material.name, collection_name: selection.collection.collection_name, brand_name: selection.collection.brand_name || '',
    material_id: selection.material.id, color_id: selection.color.id, fabric_image_url: selection.color.image_url, price_per_sqft: selection.color.price_per_sqft ?? null,
    light_mode: light.mode, light_label: light.label, openness, filter_opacity: filterOpacity, drop, ambience: effectiveAmbience,
    room_id: activeRoom.id, room_name: activeRoom.name,
    texture_url: textureUrl,
    custom_scene: activeRoom.editable ? { imageUrl: activeRoom.images.day, aspect: activeRoom.aspect, quad: activeRoom.shades[0].quad } : null,
  });

  const copyLink = async () => {
    const url = new URL(window.location.origin + '/fabrics');
    url.searchParams.set('view', 'room');
    url.searchParams.set('room', activeRoom.editable ? 'living' : activeRoom.id);
    url.searchParams.set('fabric', selection.color.code);
    if (light.mode === 'screen') url.searchParams.set('openness', openness);
    url.searchParams.set('drop', drop.toFixed(2));
    if (effectiveAmbience === 'night') url.searchParams.set('ambience', 'night');
    try {
      await navigator.clipboard.writeText(url.toString());
      toast.success('Link copied — share this look');
    } catch {
      toast.error('Could not copy link');
    }
  };

  const sendToQuote = () => {
    saveLook(buildLook());
    quoteTracker.mark('Fabric room preview: Get a Quote');
    navigate('/quote');
  };

  return (
    <div className="space-y-6" data-testid="fabric-room-preview">
      <div className="flex items-center gap-3 flex-wrap">
        {scenes.map(room => {
          const Icon = room.icon;
          const isActive = activeRoom.id === room.id;
          return (
            <button
              key={room.id}
              onClick={() => setActiveId(room.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all text-sm font-medium ${
                isActive ? 'bg-neutral-900 text-white border-neutral-900 shadow-lg' : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400 hover:bg-neutral-50'
              }`}
              data-testid={`room-btn-${room.id}`}
            >
              <Icon className="w-4 h-4" />
              {room.name}
            </button>
          );
        })}
        <div className={`ml-auto inline-flex rounded-xl border border-neutral-200 bg-white p-1 ${hasNight ? '' : 'opacity-40 pointer-events-none'}`} data-testid="ambience-toggle" title={hasNight ? 'Day / night view' : 'Day / night is available for the sample rooms'}>
          {[['day', Sun, 'Day'], ['night', Moon, 'Night']].map(([key, Icon, label]) => (
            <button
              key={key}
              onClick={() => setAmbience(key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                effectiveAmbience === key ? 'bg-neutral-900 text-white' : 'text-neutral-600 hover:bg-neutral-100'
              }`}
              data-testid={`ambience-${key}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>
        <button
          onClick={() => fileRef.current?.click()}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-dashed border-amber-400 text-amber-700 bg-amber-50 hover:bg-amber-100 transition-colors text-sm font-medium"
          data-testid="upload-room-btn"
        >
          <Upload className="w-4 h-4" />
          {customScene ? 'Replace photo' : 'Upload your room'}
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid="upload-room-input" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <div className="relative self-start bg-neutral-100 rounded-2xl overflow-hidden border border-neutral-200" data-testid="room-canvas-container">
          <RoomMockup scene={activeRoom} ambience={effectiveAmbience} textureUrl={optUrl(textureUrl, 400)} light={light} filterOpacity={filterOpacity} openness={openness} drop={drop}>
            {activeRoom.editable && <CornerEditor quad={activeRoom.shades[0].quad} onChange={onQuadChange} />}
          </RoomMockup>

          <button onClick={() => cycleRoom(-1)} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 backdrop-blur-sm shadow-lg flex items-center justify-center hover:bg-white transition-colors z-30" data-testid="room-prev-btn">
            <ChevronLeft className="w-5 h-5 text-neutral-700" />
          </button>
          <button onClick={() => cycleRoom(1)} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 backdrop-blur-sm shadow-lg flex items-center justify-center hover:bg-white transition-colors z-30" data-testid="room-next-btn">
            <ChevronRight className="w-5 h-5 text-neutral-700" />
          </button>

          {selection && (
            <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-xl px-4 py-2.5 shadow-lg flex items-center gap-3 z-30" data-testid="active-fabric-badge">
              <div className="w-10 h-10 rounded-lg overflow-hidden border border-neutral-200">
                <img src={resolveImageUrl(selection.color.image_url, 128)} alt={selection.color.name} className="w-full h-full object-cover" />
              </div>
              <div>
                <p className="font-medium text-sm text-neutral-900">{selection.color.name}</p>
                <p className="text-[11px] text-neutral-500">{selection.color.code} · {light.label}{light.mode === 'screen' ? ` ${openness}%` : ''}</p>
              </div>
              <button onClick={orderSample} className="ml-2 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-amber-50 text-amber-800 hover:bg-amber-100 text-[11px] font-medium transition-colors" data-testid="order-sample-btn" title="Order a free swatch of this fabric">
                <Scissors className="w-3.5 h-3.5" /> Free sample
              </button>
              <button onClick={() => setSelection(null)} className="p-1 hover:bg-neutral-100 rounded-full ml-1" data-testid="clear-fabric-btn">
                <X className="w-3.5 h-3.5 text-neutral-400" />
              </button>
            </div>
          )}

          {activeRoom.editable && (
            <div className="absolute bottom-4 right-4 flex items-center gap-2 z-30" data-testid="room-actions">
              <button onClick={saveRoom} disabled={savingRoom} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/90 backdrop-blur-sm shadow-lg text-xs font-medium text-neutral-800 hover:bg-white disabled:opacity-60" data-testid="save-room-btn">
                {savingRoom ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                {activeRoom.savedId ? 'Update corners' : user ? 'Save room' : 'Sign in to save'}
              </button>
              <button onClick={deleteRoom} className="p-2 rounded-lg bg-white/90 backdrop-blur-sm shadow-lg text-neutral-600 hover:text-red-600 hover:bg-white" aria-label="Remove room" data-testid="delete-room-btn">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          <div className="absolute bottom-4 left-4 bg-neutral-900/70 backdrop-blur-sm text-white px-3 py-1.5 rounded-lg text-xs font-medium z-30" data-testid="room-label">
            {activeRoom.name} · {effectiveAmbience === 'night' ? 'Night' : 'Day'} — {activeRoom.shades.length} shade{activeRoom.shades.length > 1 ? 's' : ''}
            {activeRoom.editable && ' · drag corners to fit your window'}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-neutral-200 p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Paintbrush className="w-5 h-5 text-amber-600" />
            <h3 className="font-semibold text-neutral-900">Choose Fabric</h3>
          </div>
          <p className="text-xs text-neutral-500">Select a fabric swatch below to see how it looks on the {activeRoom.name.toLowerCase()} shade{activeRoom.shades.length > 1 ? 's' : ''}.</p>

          <LightControls light={light} filterOpacity={filterOpacity} setFilterOpacity={setFilterOpacity} openness={openness} setOpenness={setOpenness} />

          {selection && (
            <div className="space-y-1.5" data-testid="drop-controls">
              <div className="flex items-center gap-2">
                <ArrowUpDown className="w-4 h-4 text-neutral-500" />
                <label className="text-[11px] text-neutral-500 uppercase tracking-wider font-medium">Shade Position</label>
              </div>
              <input type="range" min="0" max="1" step="0.01" value={drop} onChange={e => setDrop(parseFloat(e.target.value))} className="w-full accent-neutral-800 h-1.5" data-testid="drop-slider" />
              <div className="grid grid-cols-3 gap-1.5">
                {[[0, 'Open'], [0.5, 'Half'], [1, 'Close']].map(([v, label]) => (
                  <button key={label} type="button" onClick={() => setDrop(v)} className={`py-1.5 rounded-lg text-[11px] font-medium border transition-colors ${drop === v ? 'bg-neutral-900 text-white border-neutral-900' : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400'}`} data-testid={`motor-${label.toLowerCase()}`}>
                    {label}
                  </button>
                ))}
              </div>
              <div className="flex justify-between text-[10px] text-neutral-400">
                <span>Open (0%)</span>
                <span data-testid="drop-value">{Math.round(drop * 100)}% lowered</span>
                <span>Closed (100%)</span>
              </div>
            </div>
          )}

          {selection && (
            <div className="grid grid-cols-2 gap-2" data-testid="share-controls">
              <button onClick={copyLink} className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-neutral-200 text-xs font-medium text-neutral-700 hover:border-neutral-400 transition-colors" data-testid="share-copy-link-btn">
                <Link2 className="w-3.5 h-3.5" /> Copy link
              </button>
              <button onClick={sendToQuote} className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-amber-600 text-white text-xs font-medium hover:bg-amber-700 transition-colors" data-testid="share-quote-btn">
                <FileText className="w-3.5 h-3.5" /> Request quote
              </button>
            </div>
          )}

          <div className="border-t border-neutral-100 pt-3" ref={pickerRef}>
            <FabricSelector
              collections={collections}
              selectedColor={selection?.color}
              onSelect={(color, material, collection) => applySelection({ color, material, collection })}
              loading={loadingFabrics}
            />
          </div>

          {!selection && !loadingFabrics && (
            <div className="flex justify-center py-2 bg-neutral-50 rounded-xl" data-testid="gallery-preview-empty">
              <EmptyPreviewCard hint="Pick a collection and color to see it in a room" className="shadow-md !w-[92%] !px-4"
                onChooseFabric={() => pickerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />
            </div>
          )}
          {loadingFabrics && <div className="flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-neutral-300" /></div>}
        </div>
      </div>
    </div>
  );
};

export default FabricRoomPreview;
